import JobZImage from "../../../../common/jobz-img";
import { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import { jobService } from "../../../../../services/job-service";
import { useAuth } from "../../../../../context/AuthContext";
import { empEditJobRoute, empRoute, employer, publicUser } from "../../../../../globals/route-names";
import ApplicantDataTable from "../../../../common/applicant-data-table";
import { motion, MotionPanel, MotionRow, dashboardItem, dashboardPage } from "../../../../common/dashboard-motion";

function EmpManageJobsPage() {
    const { user } = useAuth();
    const [jobs, setJobs] = useState([]);
    const [message, setMessage] = useState("");
    const [loading, setLoading] = useState(true);
    const [viewingJob, setViewingJob] = useState(null);
    const [viewers, setViewers] = useState([]);
    const isAdmin = user?.role === "admin";

    const load = () => {
        setLoading(true);
        jobService.employerJobs()
            .then((data) => setJobs(data.jobs || []))
            .catch((error) => setMessage(error.message))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        load();
    }, []);

    const remove = async (jobId) => {
        try {
            await jobService.deleteEmployerJob(jobId);
            setMessage("Job deleted.");
            load();
        } catch (error) {
            setMessage(error.message);
        }
    };

    const [detailType, setDetailType] = useState(""); // "views" or "applied"
    const [detailList, setDetailList] = useState([]);

    const openDetail = async (job, type) => {
        setViewingJob(job);
        setDetailType(type);
        setDetailList([]);
        try {
            if (type === "views") {
                const data = await jobService.employerJobViews(job.id);
                setDetailList(data.viewers || []);
            } else {
                const data = await jobService.employerApplications({ jobId: job.id });
                setDetailList(data.applications || []);
            }
        } catch (error) {
            setMessage(error.message);
        }
    };

    const liveUrl = (job) => job.live_url || `${window.location.origin}/job-detail/${job.id}`;
    const previewUrl = (job) => job.preview_url || `${window.location.origin}/job-detail/${job.id}?preview=1`;

    return (
        <MotionRow className="zz-dashboard-page employer-dashboard-surface">
            <motion.div className="wt-admin-right-page-header clearfix zz-dashboard-hero" variants={dashboardItem}>
                <div className="zz-dashboard-title-row">
                    <div>
                        <h2>{isAdmin ? "All Job Listings" : "Manage Jobs"}</h2>
                        <div className="breadcrumbs"><NavLink to={publicUser.HOME1}>Home</NavLink><NavLink to={empRoute(employer.DASHBOARD)}>Dashboard</NavLink><span>{isAdmin ? "Platform Job List" : "My Job Listing"}</span></div>
                    </div>
                    {!isAdmin && (
                        <NavLink className="site-button zz-primary-action" to={empRoute(employer.POST_A_JOB)}>
                            <i className="fa fa-plus" /> Add New Job
                        </NavLink>
                    )}
                </div>
            </motion.div>
            <MotionPanel className="panel panel-default zz-jobs-panel">
                <div className="panel-heading wt-panel-heading p-a20 d-flex justify-content-between align-items-center flex-wrap gap-2">
                    <h4 className="panel-tittle m-a0"><i className="fa fa-suitcase" /> {isAdmin ? "Published Jobs" : "Job Details"}</h4>
                    {!isAdmin && <NavLink className="site-button-secondry" to={empRoute(employer.POST_A_JOB)}><i className="fa fa-edit" /> Post Job</NavLink>}
                </div>
                <div className="panel-body wt-panel-body p-a20 m-b30 ">
                    {message && <div className="alert alert-info">{message}</div>}
                    {loading ? (
                        <div className="p-a30 text-center">Loading jobs...</div>
                    ) : (
                        <div className="twm-D_table p-a20 table-responsive">
                            <table className="table table-bordered twm-bookmark-list-wrap">
                                <thead>
                                    <tr>
                                        <th>Job Title</th>
                                        <th>Category</th>
                                        {isAdmin && <th>Employer</th>}
                                        <th>Job Type</th>
                                        {isAdmin && <th>Status</th>}
                                        <th>Applications / Views</th>
                                        <th>Created &amp; Expired</th>
                                        <th>Preview / Live Link</th>
                                        <th>Action</th>
                                    </tr>
                                </thead>
                                <motion.tbody variants={dashboardPage} initial="hidden" animate="show">
                                    {jobs.length === 0 ? (
                                        <tr>
                                            <td colSpan={isAdmin ? 9 : 7} className="text-center">
                                                <div className="zz-empty-state">
                                                    <i className="fa fa-briefcase" />
                                                    <h4>No jobs posted yet.</h4>
                                                    {!isAdmin && <NavLink className="site-button" to={empRoute(employer.POST_A_JOB)}>Add New Job</NavLink>}
                                                </div>
                                            </td>
                                        </tr>
                                    ) : jobs.map((job, index) => (
                                        <motion.tr key={job.id} variants={dashboardItem} className="zz-job-row">
                                            <td>
                                                <div className="twm-bookmark-list">
                                                    <div className="twm-media">
                                                        <div className="twm-media-pic">
                                                            <JobZImage src={job.job_image_url || job.company_logo_url || `images/jobs-company/pic${(index % 5) + 1}.jpg`} alt={job.title} />
                                                        </div>
                                                    </div>
                                                    <div className="twm-mid-content">
                                                        <a href={job.status === "published" ? liveUrl(job) : previewUrl(job)} className="twm-job-title" target="_blank" rel="noreferrer">
                                                            <h4>{job.title}</h4>
                                                            <p className="twm-bookmark-address"><i className="feather-map-pin" />{job.location}</p>
                                                        </a>
                                                        {job.status !== "published" && <small className="text-warning d-block">Draft preview until admin approval</small>}
                                                        {job.status === "published" && <small className="site-text-primary d-block">Live: {liveUrl(job)}</small>}
                                                    </div>
                                                </div>
                                            </td>
                                            <td>{job.category}</td>
                                            {isAdmin && <td>{job.company_name || job.employer_name || "Employer"}</td>}
                                            <td><div className="twm-jobs-category zz-job-type-pill"><span className="twm-bg-green"><i className="fa fa-briefcase" /> {job.job_type}</span></div></td>
                                            {isAdmin && <td>{job.status}</td>}
                                            <td>
                                                <button className="btn btn-sm btn-link p-0 fw-bold d-block text-start mb-1" type="button" onClick={() => openDetail(job, "applied")}>
                                                    {job.application_count || 0} Applied
                                                </button>
                                                <div className="zz-mini-chart" aria-hidden="true"><span /><span /><span /><span /><span /></div>
                                                <button className="btn btn-sm btn-link p-0 d-block text-start" type="button" onClick={() => openDetail(job, "views")}>
                                                    {job.view_count || 0} Views
                                                </button>
                                            </td>
                                            <td>
                                                <div>{formatDate(job.created_at)}</div>
                                                <div>{job.expires_at || "Open"}</div>
                                            </td>
                                            <td>
                                                <div className="d-flex flex-column gap-1">
                                                    <a className="site-text-primary fw-bold" href={previewUrl(job)} target="_blank" rel="noreferrer">
                                                        Preview job display
                                                    </a>
                                                    <small className="text-muted text-break">{previewUrl(job)}</small>
                                                    {job.status === "published" ? (
                                                        <>
                                                            <a className="text-success fw-bold" href={liveUrl(job)} target="_blank" rel="noreferrer">
                                                                Live job link
                                                            </a>
                                                            <small className="text-muted text-break">{liveUrl(job)}</small>
                                                        </>
                                                    ) : (
                                                        <small className="text-warning">Waiting for admin approval. Preview works now.</small>
                                                    )}
                                                </div>
                                            </td>
                                            <td>
                                                <div className="twm-table-controls">
                                                    <ul className="twm-DT-controls-icon list-unstyled zz-action-icons">
                                                        <li>
                                                            <a title={job.status === "published" ? "Open Live Job" : "Open Draft Preview"} href={job.status === "published" ? liveUrl(job) : previewUrl(job)} target="_blank" rel="noreferrer">
                                                                <span className={job.status === "published" ? "fa fa-external-link-alt" : "far fa-eye"} />
                                                            </a>
                                                        </li>
                                                        {!isAdmin && (
                                                            <li>
                                                                <NavLink title="Edit Job" to={empEditJobRoute(job.id)}>
                                                                    <span className="fa fa-edit" />
                                                                </NavLink>
                                                            </li>
                                                        )}
                                                        <li>
                                                            <button title="Delete" type="button" onClick={() => remove(job.id)}>
                                                                <span className="far fa-trash-alt" />
                                                            </button>
                                                        </li>
                                                    </ul>
                                                </div>
                                            </td>
                                        </motion.tr>
                                    ))}
                                </motion.tbody>
                            </table>
                        </div>
                    )}
                </div>
            </MotionPanel>
            {viewingJob && (
                <MotionPanel className="panel panel-default">
                    <div className="panel-heading wt-panel-heading p-a20">
                        <h4 className="panel-tittle m-a0"><i className={detailType === "views" ? "fa fa-eye" : "fa fa-file-alt"} /> {detailType === "views" ? `Candidate Views: ${viewingJob.title}` : `Job Applicants: ${viewingJob.title}`}</h4>
                    </div>
                    <div className="panel-body wt-panel-body p-a20">
                        <ApplicantDataTable 
                            applications={detailList} 
                            title={detailType === "views" ? `${viewingJob.title} Viewers` : `${viewingJob.title} Applicants`} 
                            emptyText={detailType === "views" ? "No logged-in candidate views recorded yet." : "No candidates have applied for this job yet."} 
                        />
                        <button className="site-button-secondry m-t15" type="button" onClick={() => { setViewingJob(null); setDetailType(""); setDetailList([]); }}>Close List</button>
                    </div>
                </MotionPanel>
            )}
        </MotionRow>
    )
}

function formatDate(value) {
    if (!value) return "";
    return new Date(value).toLocaleDateString();
}

export default EmpManageJobsPage;
